// src/pages/notifications/NotificationsPage.jsx
//
// Full notifications list — destination of the "View all notifications"
// link in NotificationDropdown. Mirrors the dropdown's styling but
// uses full-width rows, groups by date, and pulls up to 50.

import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Check, Zap, FileText, Ticket, Bell as BellIcon,
  AlertTriangle, MessageSquare,
} from "lucide-react";
import MessengerLayout from "../../layout/MessengerLayout";
import { Skeleton, ErrorState, EmptyState } from "../../components/ui/LoadingStates";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const NEON       = "#00FF99";
const PANEL_BG   = "#0a0a0a";
const PANEL_BORD = "#1a1a1a";
const MUTED      = "#666";
const TEXT       = "#fff";
const TIME_MUTED = "#444";

function iconForType(t) {
  const k = (t || "").toLowerCase();
  if (k.includes("approv") || k === "leave_approved" || k === "success") return { I: Check, tint: "#22c55e" };
  if (k.includes("kira") || k === "action")    return { I: Zap, tint: NEON };
  if (k.includes("polic"))                     return { I: FileText, tint: "#a78bfa" };
  if (k.includes("ticket"))                    return { I: Ticket, tint: "#fb923c" };
  if (k.includes("warn"))                      return { I: AlertTriangle, tint: "#f59e0b" };
  if (k.includes("dm") || k.includes("message")) return { I: MessageSquare, tint: "#60a5fa" };
  return { I: BellIcon, tint: MUTED };
}

function getNotifRoute(n) {
  const link = n?.link;
  if (typeof link !== "string" || !link.startsWith("/")) return null;
  return link;
}

function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso);
  if (isNaN(then.getTime())) return "";
  const now = new Date();
  const diff = (now - then) / 1000;
  if (diff < 60)               return "just now";
  if (diff < 60 * 60)          return `${Math.floor(diff / 60)}m ago`;
  if (diff < 60 * 60 * 24)     return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 60 * 60 * 24 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return then.toLocaleDateString();
}

// Date-based grouping uses calendar boundaries, not "24h ago", so the
// "Today" bucket always means today's local date.
function groupKey(iso) {
  if (!iso) return "Older";
  const then = new Date(iso);
  if (isNaN(then.getTime())) return "Older";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const ymdThen = new Date(then.getFullYear(), then.getMonth(), then.getDate());
  const diffDays = Math.floor((today - ymdThen) / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7)   return "This week";
  return "Older";
}

const GROUP_ORDER = ["Today", "Yesterday", "This week", "Older"];

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [notifs,  setNotifs]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const load = useCallback(() => {
    const tok = localStorage.getItem("token");
    if (!tok) { setLoading(false); setError("Not signed in"); return; }
    setLoading(true); setError(null);
    fetch(`${API_BASE}/api/notifications?limit=50`, {
      headers: { Authorization: `Bearer ${tok}` },
    })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d => {
        const list = Array.isArray(d) ? d : (d?.items || d?.notifications || d?.data || []);
        setNotifs(list);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const markAllRead = async () => {
    const tok = localStorage.getItem("token");
    if (!tok) return;
    try {
      await fetch(`${API_BASE}/api/notifications/read-all`, {
        method: "POST",
        headers: { Authorization: `Bearer ${tok}` },
      });
      setNotifs(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch {
      // Silent — mark-read is a routine background action; we don't toast on
      // failure. Empty catch is intentional so the page doesn't crash.
    }
  };

  const handleItemClick = async (n) => {
    const tok = localStorage.getItem("token");
    if (tok && !n.is_read) {
      try {
        await fetch(`${API_BASE}/api/notifications/${n.id}/read`, {
          method: "PATCH",
          headers: { Authorization: `Bearer ${tok}` },
        });
        setNotifs(prev => prev.map(x => x.id === n.id ? { ...x, is_read: true } : x));
      } catch {
        // Silent — routine mark-as-read; don't toast. Empty catch is
        // intentional so a failed PATCH doesn't block navigation.
      }
    }
    const route = getNotifRoute(n);
    if (route) navigate(route);
  };

  const grouped = useMemo(() => {
    const groups = { Today: [], Yesterday: [], "This week": [], Older: [] };
    notifs.forEach(n => {
      const k = groupKey(n.created_at);
      groups[k].push(n);
    });
    return groups;
  }, [notifs]);

  return (
    <MessengerLayout>
      <div className="text-white" style={{ maxWidth: 760, margin: "0 auto", padding: "16px 16px 32px" }}>
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          marginBottom: 20, gap: 12, flexWrap: "wrap",
        }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 500, color: TEXT, margin: 0 }}>
              Notifications
            </h1>
            <p style={{ fontSize: 12, color: "#555", margin: "4px 0 0 0" }}>
              {loading ? "Loading…" : `${notifs.length} total · ${notifs.filter(n => !n.is_read).length} unread`}
            </p>
          </div>
          <button
            type="button"
            onClick={markAllRead}
            disabled={loading || notifs.every(n => n.is_read)}
            style={{
              background: "transparent",
              border: `0.5px solid ${NEON}40`,
              color: NEON, fontSize: 12,
              padding: "6px 14px", borderRadius: 8,
              cursor: "pointer",
              opacity: (loading || notifs.every(n => n.is_read)) ? 0.5 : 1,
            }}
          >
            Mark all read
          </button>
        </div>

        {/* Body */}
        {loading ? (
          <SkeletonRows />
        ) : error ? (
          <ErrorState message="Couldn't load notifications" detail={error} onRetry={load} />
        ) : notifs.length === 0 ? (
          <EmptyState
            icon={BellIcon}
            title="No notifications yet"
            body="When something needs your attention it will show up here."
          />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            {GROUP_ORDER.map(g => {
              const items = grouped[g];
              if (!items || items.length === 0) return null;
              return (
                <section key={g}>
                  <div style={{
                    fontSize: 11, color: "#444", textTransform: "uppercase",
                    letterSpacing: 1.2, marginBottom: 8,
                  }}>
                    {g}
                  </div>
                  <div style={{
                    background: PANEL_BG, border: `0.5px solid ${PANEL_BORD}`,
                    borderRadius: 12, overflow: "hidden",
                  }}>
                    {items.map((n, i) => {
                      const { I, tint } = iconForType(n.type);
                      return (
                        <div
                          key={n.id}
                          onClick={() => handleItemClick(n)}
                          style={{
                            cursor: "pointer",
                            padding: "14px 16px",
                            borderLeft: n.is_read ? "2px solid transparent" : `2px solid ${NEON}`,
                            borderBottom: i === items.length - 1 ? "none" : `0.5px solid ${PANEL_BORD}`,
                            opacity: n.is_read ? 0.6 : 1,
                            display: "flex", gap: 12,
                            transition: "background 0.12s",
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = "#0d0d0d"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                        >
                          <div style={{
                            width: 32, height: 32, borderRadius: "50%",
                            background: "#0d0d0d",
                            border: `0.5px solid ${tint}33`,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            color: tint, flexShrink: 0, marginTop: 2,
                          }}>
                            <I size={15} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            {n.title && (
                              <div style={{
                                fontSize: 14, color: TEXT, fontWeight: 500,
                                lineHeight: 1.4,
                              }}>{n.title}</div>
                            )}
                            {n.body && (
                              <div style={{
                                fontSize: 13, color: MUTED, marginTop: 4,
                                lineHeight: 1.55, whiteSpace: "pre-wrap",
                              }}>{n.body}</div>
                            )}
                            <div style={{ fontSize: 11, color: TIME_MUTED, marginTop: 6 }}>
                              {relativeTime(n.created_at)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </MessengerLayout>
  );
}

function SkeletonRows() {
  return (
    <div style={{
      background: PANEL_BG, border: `0.5px solid ${PANEL_BORD}`,
      borderRadius: 12, padding: 8,
    }}>
      {[1, 2, 3, 4, 5].map(i => (
        <div key={i} style={{
          padding: "14px 12px",
          borderBottom: i === 5 ? "none" : `0.5px solid ${PANEL_BORD}`,
          display: "flex", gap: 12, alignItems: "flex-start",
        }}>
          <Skeleton width={32} height={32} radius="50%" />
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
            <Skeleton width="55%" height={12} />
            <Skeleton width="85%" height={10} />
            <Skeleton width="20%" height={9} />
          </div>
        </div>
      ))}
    </div>
  );
}
